/**
 * Schedule View — liveness helpers.
 *
 * ⛔ KEEP THIS FILE PURE AND IMPORT-FREE. cypress/node-tests executes it verbatim, the same way it
 * executes phiMask.js and the other scheduleView utils. One `import` makes it unloadable there and
 * the guards silently stop running.
 *
 * ── WHAT "LIVE" MEANS HERE ────────────────────────────────────────────────────────────────────
 * An office leaves this page open on an operatory iPad all day and expects it to track Open Dental
 * as things happen — somebody marked arrived, an appointment completed, a slot broken — without
 * pressing Refresh. If staff have to refresh, the page stops being a schedule and becomes a
 * report, which defeats the point of building it.
 *
 * The page cannot simply re-run `/day` on a short timer: that is six joined queries, and running
 * them every few seconds per open tab would multiply the cost of the whole feature to discover,
 * almost every time, that nothing changed. So it asks a cheap question often (`/pulse`, three
 * indexed aggregates returning four numbers) and does the expensive thing only when the answer
 * moves. This file holds the two pure decisions in that loop, kept out of the component so they
 * can be tested without mounting anything:
 *
 *   dayChanged()   — is the version we just received different from the one we drew?
 *   describeSync() — what, if anything, should the page say about its own freshness?
 */

/**
 * Has the day changed since the version the page is currently drawing?
 *
 * EQUALITY, deliberately — never "is this newer". The version's appointment half is
 * `MAX(DateTStamp)` paired with `COUNT(*)`, and MAX legitimately moves BACKWARDS when the
 * newest-stamped row is deleted. A comparison that only accepted forward movement would treat
 * "somebody just deleted the appointment that was edited most recently" as nothing happening —
 * which is a real Tuesday, not a hypothetical.
 *
 * A null/absent version on either side means "nothing to compare": the first poll after load, or
 * a site with no Open Dental mirror yet. Both must answer FALSE rather than true, or the page
 * would refetch `/day` on every poll forever — hardest on exactly the sites that have no data.
 */
export function dayChanged(previous, next) {
  if (!previous || !next) return false;
  return previous !== next;
}

/**
 * Should this pulse response cause a refetch?
 *
 * Folded into one place because the two reasons are easy to conflate at a call site. A pulse for a
 * DIFFERENT date than the one on screen must never trigger a refetch: the poll and a date change
 * race every time somebody clicks "next day", and honouring a stale in-flight answer would refetch
 * yesterday over the top of today.
 */
export function shouldRefetch({ currentDate, currentVersion, pulse }) {
  if (!pulse) return false;
  if (!pulse.date || pulse.date !== currentDate) return false;
  return dayChanged(currentVersion, pulse.v);
}

/** Round a duration to whole minutes, never reporting "0 minutes" for a real gap. */
function minutesFrom(seconds) {
  return Math.max(1, Math.round(seconds / 60));
}

/**
 * What the page says about its own freshness.
 *
 * ── WHY "LIVE" IS SILENT ──────────────────────────────────────────────────────────────────────
 * The healthy state deliberately produces NO sentence about minutes. The obvious design — always
 * showing "last synced N minutes ago" — reads as a warning even when everything is fine, because
 * the Open Dental heartbeat only arrives every ~10 minutes: a perfectly healthy practice would
 * spend most of its day looking at "last synced 8 minutes ago" on a page whose entire promise is
 * that it is current. Staff would either distrust a page that is correct, or learn to ignore the
 * line entirely — and then it says nothing on the day it is real. So a healthy sync says "Live",
 * and the number appears only when the number is the point.
 *
 * ── WHY `unknown` IS NOT `stale` ──────────────────────────────────────────────────────────────
 * A site with no heartbeat yet has not been proven behind. Claiming staleness would put a warning
 * on a page that may be perfectly current; claiming freshness would suppress a real one. Saying
 * plainly that we cannot tell is the only honest option, and it is rare enough to be worth the
 * third state.
 *
 * @param {{state: string, ageSeconds: number|null}|null} sync  the `/pulse` sync block
 * @returns {{tone: string, label: string, detail: string}}
 */
export function describeSync(sync) {
  const state = sync && sync.state;

  if (state === 'live') {
    return {
      tone: 'live',
      label: 'Live',
      detail: 'This schedule is following Open Dental. You do not need to refresh.',
    };
  }

  if (state === 'stale') {
    const age = (sync && typeof sync.ageSeconds === 'number') ? sync.ageSeconds : null;
    const mins = age === null ? null : minutesFrom(age);
    const howLong = mins === null
      ? 'for a while'
      : (mins >= 120 ? `for over ${Math.floor(mins / 60)} hours` : `for ${mins} minutes`);
    return {
      tone: 'stale',
      label: mins === null ? 'Not syncing' : `Last synced ${mins} min ago`,
      // Names the cause AND the consequence. "Stale data" alone leaves the reader to guess
      // whether that means a slow page or a schedule that is missing this morning's changes.
      detail: `Teamio has not heard from this practice's Open Dental ${howLong}. `
        + 'Appointments changed in Open Dental since then will not appear here yet.',
    };
  }

  return {
    tone: 'unknown',
    label: 'Sync status unknown',
    detail: 'Teamio cannot currently confirm when this schedule last updated from Open Dental.',
  };
}

/**
 * How long to wait before the next pulse.
 *
 * A hidden tab is not polled at all — an iPad on a shelf with the screen off, or a browser tab
 * behind twelve others, is spending the practice's budget on a screen nobody is reading. The page
 * checks immediately on becoming visible instead, so nothing is lost by pausing.
 *
 * The visible interval is the feature's main cost lever: it is multiplied by every open tab in
 * every practice, and unlike the Lambda itself the API Gateway charge does not shrink with a
 * faster query. 30 seconds is the deliberate compromise — fast enough that a confirmation or an
 * arrival lands while somebody is still looking at the screen, slow enough to be affordable
 * across the fleet. Sub-second updates are the exe accelerator's job (Phase 3Lb), not the poll's.
 *
 * ── ADAPTIVE (Schedule - Chart Audit, 2026-10-28) ─────────────────────────────────────────────
 * Every office that can open the page now has Teamio app 3.0.61+, so it has the live signal — and
 * the signal, not the poll, is what makes the page feel instant. While the signal is HEALTHY the poll
 * is only the safety net behind it and runs every PULSE_HEALTHY_INTERVAL_MS (5 minutes); the moment
 * it is not (no listener, listener failed or rules denied, or the freshness indicator is not "Live"
 * — the exe's heartbeat went silent past its bar) it goes back to the 30 seconds above, because then
 * the poll is the only thing keeping the page current. That cuts the check's cost, and the prod DB
 * load behind it, about tenfold. See isSignalHealthy.
 */
export const PULSE_INTERVAL_MS = 30000;
export const PULSE_HEALTHY_INTERVAL_MS = 300000;
export const PULSE_HIDDEN_INTERVAL_MS = 0;

/**
 * Is the live signal healthy enough to relax the poll?
 *
 * ⛔ NOT a second notion of health: it is the two the page already has, both required —
 *   • `listening` — the Firestore signal listener (store watchSignal) has delivered a snapshot and
 *     has not failed since (store state.signalListening; false when it was never opened, was denied
 *     by the rules, or errored);
 *   • `syncState` — the freshness indicator's own state (describeSync: 'live' | 'stale' | 'unknown'),
 *     i.e. the exe's heartbeat is inside its bar. 'unknown' (nothing heard yet) is NOT healthy.
 * Strictly true/'live': anything else, including a missing input, keeps the fast poll.
 */
export function isSignalHealthy({ listening, syncState } = {}) {
  return listening === true && syncState === 'live';
}

/**
 * How long to wait before the next pulse. `healthy` is isSignalHealthy(...); it defaults to false so a
 * caller that does not know keeps the fast poll.
 */
export function nextPulseDelay(isVisible, healthy = false) {
  if (!isVisible) return PULSE_HIDDEN_INTERVAL_MS;
  return healthy === true ? PULSE_HEALTHY_INTERVAL_MS : PULSE_INTERVAL_MS;
}

/**
 * "Something changed that could affect any day" — the exe's key for a table with no date of its
 * own (an operatory renamed, a blockout re-assigned to a different chair). Must match
 * `ANY_DATE` in teamio-exe/main-process/app/schedule-view-signal.js.
 */
export const ANY_DATE = '*';

/**
 * Does this Firestore signal say the day on screen changed? (Phase 3Lb)
 *
 * ── WHAT THE SIGNAL IS, AND IS NOT ────────────────────────────────────────────────────────────
 * The office's own machine watches Open Dental's binlog and bumps a counter per date in one
 * Firestore document. That is ALL it carries — dates and counters, never a patient. Firestore
 * bypasses the PHI demo mask, so the schedule itself must keep arriving through axios; this only
 * decides WHEN to ask for it.
 *
 * ── ⛔ COMPARE AGAINST WHAT WE LAST SAW, NOT AGAINST ZERO ─────────────────────────────────────
 * The first snapshot after subscribing delivers the document's CURRENT state, which is a running
 * total from every change the practice has made today. Treating that as "it changed" would make
 * every page load refetch the day it had just fetched — and on a busy morning, over and over.
 * So the first snapshot only establishes a baseline; a refetch needs a counter to move PAST it.
 *
 * ── AND NOT AGAINST "GREATER THAN" EITHER ─────────────────────────────────────────────────────
 * `!==`, not `>`. The counter is monotonic on the server, but a document can be recreated — and a
 * page that only accepted increases would then be permanently deaf to that site's signals, with
 * nothing on screen saying so. Any movement means ask.
 */
export function signalChangedDate(previous, next, date) {
  if (!next || !date) return false;
  // No baseline yet: adopt, never act. See above.
  if (!previous) return false;

  const before = previous[date];
  const after = next[date];
  if (after !== undefined && before !== after) return true;

  const anyBefore = previous[ANY_DATE];
  const anyAfter = next[ANY_DATE];
  return anyAfter !== undefined && anyBefore !== anyAfter;
}

/**
 * The counters out of a raw Firestore document, defensively.
 *
 * ⛔ Reads ONLY the `dates` map and only its numeric values. A document is shared, mutable state
 * written by a desktop application the browser does not control, so anything unexpected in it is
 * ignored rather than trusted — and nothing but numbers is ever read out of it.
 */
export function readSignalCounters(doc) {
  const dates = (doc && doc.dates && typeof doc.dates === 'object') ? doc.dates : null;
  if (!dates) return null;
  const out = {};
  Object.keys(dates).forEach((key) => {
    const value = dates[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  });
  return out;
}

/**
 * The signal chase (2026-09-25): after an exe signal, keep asking the pulse briefly until the
 * version moves.
 *
 * ── WHY ONE PULSE PER SIGNAL WAS NOT ENOUGH ───────────────────────────────────────────────────
 * The exe writes the signal ~1s after Open Dental's binlog moves, but the pulse reads the CLOUD
 * MIRROR, which the exe feeds through a 5-second SQS batch → qprocessor → MySQL: ≈5–7s end to end.
 * So the signal almost always lands BEFORE the mirror has the change, the pulse honestly answers
 * "nothing moved", and the page sat on the old schedule until a later bump or the 30s poll —
 * measured on Mint 2026-09-25 at 5–10s, and up to 30s for a single-bump change.
 *
 * ── WHY THESE NUMBERS ─────────────────────────────────────────────────────────────────────────
 * Every 2s for at most 6 retries: a ≈12s window after the immediate tick, which covers the 5–7s
 * mirror with margin and ends well inside the 30s poll that remains the correctness guarantee. A
 * tighter interval buys nothing (the mirror cannot be ready sooner) and multiplies pulses by every
 * open tab that got the bump; a looser one becomes the latency the chase exists to remove.
 */
export const SIGNAL_CHASE_INTERVAL_MS = 2000;
export const SIGNAL_CHASE_MAX_TRIES = 6;

/**
 * After a chase tick: 'done' (every nudge has been redrawn), 'stop' (give up and leave it to the
 * poll), or 'retry' (ask again in SIGNAL_CHASE_INTERVAL_MS).
 *   `bumps` — signals received during this chase (1 at the start, +1 per bump while chasing);
 *   `moves` — redraws seen since the chase started (see noteChaseRedraw);
 *   `tries` — RETRIES made since the latest bump. The immediate tick at a bump is not one.
 *
 * ── WHY THE DATE COMES FIRST ──────────────────────────────────────────────────────────────────
 * A signal is about the day it arrived for. Once somebody moves to another date, a redraw on screen
 * belongs to THAT day and says nothing about this bump.
 *
 * ── WHY "DONE" IS AS MANY REDRAWS AS NUDGES, NOT THE FIRST MOVE ───────────────────────────────
 * Two changes a few seconds apart ("confirmed", then "arrived") are two bumps, and usually two
 * mirror batches. Ending on the FIRST version move drew change A and quit while B was still on its
 * way to the mirror — B then waited out the 30s poll, which is the exact latency the chase is for.
 * The same happens to ONE booking whose rows straddle two 5s batches. So the chase waits until the
 * page has redrawn at least once per nudge. It does not matter WHO redrew — this chase, the 30s
 * poll, the tab regaining focus or a manual Refresh: each is a real redraw and counts.
 *
 * KNOWN, ACCEPTED: when several bumps collapse into ONE mirror move (both rows of a booking in the
 * same batch), the redraws never catch up with the nudges, so the chase runs out its window after
 * the change is already drawn — at most SIGNAL_CHASE_MAX_TRIES wasted pulses.
 *
 * ── WHY A HIDDEN TAB STOPS ────────────────────────────────────────────────────────────────────
 * Same reason the poll skips one: nobody is reading it, and the page re-ticks the moment the tab
 * becomes visible, so nothing is lost.
 *
 * ── WHY IT ALWAYS ENDS ────────────────────────────────────────────────────────────────────────
 * KNOWN, ACCEPTED: the exe also signals operatory / scheduleop changes (as ANY_DATE), and the pulse
 * version is built from appointment/schedule rows only — it can never see those. For them the chase
 * runs its whole window and stops (≤ SIGNAL_CHASE_MAX_TRIES wasted pulses), and the 30s poll
 * behaves exactly as it did before the chase. Malformed counts stop too: a chase that cannot count
 * must not be able to spin.
 */
export function nextChaseStep({ tries, startDate, currentDate, bumps, moves, hidden } = {}) {
  if (currentDate !== startDate || !currentDate) return 'stop';
  if (typeof bumps !== 'number' || !Number.isFinite(bumps)
    || typeof moves !== 'number' || !Number.isFinite(moves)) return 'stop';
  if (moves >= bumps) return 'done';
  if (hidden) return 'stop';
  if (typeof tries !== 'number' || !(tries < SIGNAL_CHASE_MAX_TRIES)) return 'stop';
  return 'retry';
}

/**
 * Count a redraw: `{ moves, lastSeenVersion }` after looking at the version now drawn. Returns a new
 * object; never mutates the one given.
 *
 * `!==`, the same rule as dayChanged — a version moving BACKWARDS (the newest-stamped row deleted)
 * is a redraw too. Seeing the same version twice counts once.
 *
 * ⛔ ADOPTING a first version is NOT a redraw. With nothing drawn yet (page load, or just after a
 * date change) the first pulse merely records the mirror's version — and that version can predate
 * the very change the chase was nudged about, so counting it would end the chase before the change
 * is drawn. Likewise a version CLEARED by a date change is nothing seen: the date rule decides that.
 */
export function noteChaseRedraw({ moves, lastSeenVersion } = {}, drawnVersion) {
  if (!drawnVersion) return { moves, lastSeenVersion };
  if (!lastSeenVersion) return { moves, lastSeenVersion: drawnVersion };
  if (drawnVersion !== lastSeenVersion) return { moves: moves + 1, lastSeenVersion: drawnVersion };
  return { moves, lastSeenVersion };
}

/**
 * Wait this long after an alert bump before re-asking, so a burst becomes ONE request.
 * A three-section packet is three FORM_SUBMISSION events inside ~3 seconds; without this the page
 * would ask `/alerts` three times for the same answer.
 *
 * 1000ms, down from 1500 (2026-09-24), once sends started bumping at the API: the debounce is now
 * the largest fixed cost between a click and the icon. A packet whose sections land more than a
 * second apart can now cost two requests instead of one — each a keyed `/alerts` call for one rule.
 */
export const ALERT_SIGNAL_DEBOUNCE_MS = 1000;

/**
 * The per-ALERT counters out of the signal document (Phase 3 live refresh, 2026-09-23).
 *
 * The cloud writes `alerts.<alertKey>` when a Teamio-side event changes that alert's data — a form
 * coming back bumps `alerts.forms` (teamio-utility lib/schedule-view-signal.js). It is a SIBLING of
 * the exe's `dates` map on the same document.
 *
 * ⛔ Whitelisted to `allowedKeys` AND numbers only. The document is shared, mutable state the
 * browser does not control; a stray key must never reach `/alerts`, which answers an unknown key
 * with a 400 — one bogus field would silently kill every live refresh for the site.
 *
 * Always an object (never null): a document with no `alerts` map — every site that has had no
 * bump yet today, and the document not existing at all — is simply "all counters absent". The
 * baseline question is the caller's (see changedAlertKeys), so a first bump is never mistaken for
 * the first snapshot.
 */
export function readAlertCounters(doc, allowedKeys) {
  const alerts = (doc && doc.alerts && typeof doc.alerts === 'object') ? doc.alerts : null;
  const out = {};
  if (!alerts || !Array.isArray(allowedKeys)) return out;
  allowedKeys.forEach((key) => {
    const value = alerts[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  });
  return out;
}

/**
 * Which alerts' counters moved since last seen. Same rules as signalChangedDate: no baseline ⇒
 * adopt it and ask for nothing; `!==` rather than `>`, so a recreated document still counts.
 * A key that appears for the first time (absent → 1) is a change — that is the first bump of the
 * day, and on most sites the only kind there is.
 */
export function changedAlertKeys(previous, next) {
  if (!previous || !next) return [];
  return Object.keys(next).filter(key => previous[key] !== next[key]);
}
